/**
 * Cmd+K / Ctrl+K: paleta de comandos com os primitivos do Macro
 * (CommandMenuShell/List/SearchInput + createCommandListController) dentro do Dialog do Macro.
 */
import { createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { Dialog } from '@ui/components/Dialog'
import {
  CommandMenuEmptyState,
  CommandMenuList,
  CommandMenuListItem,
  CommandMenuSearchInput,
  CommandMenuShell,
  createCommandListController,
} from '@ui/components/CommandMenuPrimitives'
import { registrarAtalho, prettyPrintHotkeyString } from '@core/hotkey/utils'

export interface Comando {
  id: string
  titulo: string
  dica?: string
  desabilitado?: boolean
  executar: () => void
}

// Normaliza para busca: sem acento, minúsculas, pontuação vira espaço.
const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

registrarAtalho('abrir-comandos', 'cmd+k')

export function Comandos(props: { comandos: () => Comando[] }) {
  const [aberto, setAberto] = createSignal(false)
  const [busca, setBusca] = createSignal('')

  const itens = createMemo(() => {
    // Todas as palavras digitadas precisam aparecer (em qualquer ordem): "tema claro" acha "Tema: Claro".
    const palavras = normalizar(busca()).split(' ').filter(Boolean)
    const todos = props.comandos()
    if (palavras.length === 0) return todos
    return todos.filter((c) => {
      const alvo = normalizar(`${c.titulo} ${c.dica ?? ''}`)
      return palavras.every((p) => alvo.includes(p))
    })
  })

  const executar = (c: Comando) => {
    if (c.desabilitado) return
    setAberto(false)
    c.executar()
  }
  const lista = createCommandListController({ items: itens, onSelect: executar })

  let campo: HTMLInputElement | undefined
  const abrir = () => {
    setBusca('')
    lista.setSelectedIndex(0)
    setAberto(true)
  }
  // O Dialog (Kobalte) move o foco para dentro dele ao abrir; trocamos esse foco
  // padrão pelo campo de busca (bug achado no teste: o texto digitado se perdia).
  const focarBusca = (e: Event) => {
    e.preventDefault()
    campo?.focus()
  }

  onMount(() => {
    const tecla = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (aberto()) setAberto(false)
        else abrir()
      }
    }
    window.addEventListener('keydown', tecla)
    onCleanup(() => window.removeEventListener('keydown', tecla))
  })

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        class="hidden sm:flex items-center gap-2 h-8 px-3 rounded-full border border-edge text-xs text-ink-subtle hover:bg-hover"
      >
        Buscar ou ir para…
        <kbd class="font-mono text-ink-placeholder">{prettyPrintHotkeyString('cmd+k')}</kbd>
      </button>
      <Dialog
        open={aberto()}
        onOpenChange={setAberto}
        onOpenAutoFocus={focarBusca}
        class="w-[min(560px,calc(100vw-32px))] p-0"
      >
        <CommandMenuShell>
          <CommandMenuShell.Header>
            <CommandMenuSearchInput
              ref={(el: HTMLInputElement) => (campo = el)}
              placeholder="Ir para um módulo ou executar um comando…"
              value={busca()}
              onInput={(e) => {
                setBusca(e.currentTarget.value)
                lista.setSelectedIndex(0)
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  lista.selectNext()
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  lista.selectPrevious()
                } else if (e.key === 'Enter') {
                  e.preventDefault()
                  lista.selectSelected()
                }
              }}
            />
          </CommandMenuShell.Header>
          <CommandMenuShell.Body scroll class="max-h-[50vh] p-1">
            <Show when={itens().length > 0} fallback={<CommandMenuEmptyState>Nada encontrado.</CommandMenuEmptyState>}>
              <CommandMenuList
                items={itens()}
                selectedIndex={lista.selectedIndex()}
                scrollSelectedIntoView={lista.shouldScrollSelectedIntoView()}
                itemDisabled={(c) => !!c.desabilitado}
                onSelect={(c) => executar(c)}
                onItemMouseMove={(i) => lista.setSelectedIndexFromPointer(i)}
              >
                {(c, i) => (
                  <CommandMenuListItem as="div" selected={lista.isSelected(i())} disabled={c.desabilitado}>
                    <span class="flex-1 text-ink">{c.titulo}</span>
                    <Show when={c.dica}>
                      <span class="text-xs font-normal text-ink-placeholder">{c.dica}</span>
                    </Show>
                  </CommandMenuListItem>
                )}
              </CommandMenuList>
            </Show>
          </CommandMenuShell.Body>
        </CommandMenuShell>
      </Dialog>
    </>
  )
}
